import h from 'hyperscript';

import { px } from 'cadenza/utils/px';

import './sketch-text-input.css';

export class SketchTextInput extends HTMLElement {

  constructor (style) {
    super();
    if (style) {
      this.setStyle(style);
    }
  }

  connectedCallback () {
    this.classList.add('d-sketch-text-input');
    this.innerHTML = '';

    this._applyStyle();
    this.hidden = true;

    this._textInput = h('span', {
      contentEditable: true,
      onkeydown: event => this._onKeyDown(event),
      onkeypress: event => event.stopPropagation(),
      onblur: () => this.cancel()
    });
    this.append(' ', this._textInput); // additional space before text input fixes minimal height problem in Firefox
  }

  setStyle (ol5Style) {
    this._style = createTextEditorStyle(ol5Style);
    this._applyStyle();
  }

  _applyStyle () {
    if (this.style && this._style) {
      Object.keys(this._style).forEach(key => {
        this.style[key] = this._style[key];
      });
    }
  }

  show ([ x, y ], text = '') {
    this.position = [ x, y ];
    this._textInput.textContent = text;
    this.hidden = false;
    this._textInput.focus();
    placeCaretAtEnd(this._textInput);
  }

  set position ([ x, y ]) {
    this.style.left = px(x);
    this.style.top = px(y);
  }

  hide () {
    this._textInput.textContent = '';
    this.hidden = true;
  }

  isShown () {
    return !this.hidden;
  }

  _onKeyDown (event) {
    event.stopPropagation();
    if (event.key === 'Enter' && !event.shiftKey) {
      this.done();
    } else if (event.key === 'Escape' || event.key === 'Esc' /* IE11 */) {
      this.cancel();
    }
  }

  done () {
    // I know, I know, it's all bad, but that's the only way how I can get line breaks in firefox.
    // eslint-disable-next-line no-restricted-syntax
    const text = this._textInput.innerText;
    if (text) {
      this.dispatchFinish(text);
      this.hide();
    } else {
      this.cancel();
    }
  }

  cancel () {
    this.dispatchCancel();
    this.hide();
  }

  dispatchCancel () {
    return this.dispatchEvent(new CustomEvent('cancel'));
  }

  dispatchFinish (text) {
    return this.dispatchEvent(new CustomEvent('finish', { detail: { text } }));
  }

}

customElements.define('d-sketch-text-input', SketchTextInput);

function createTextEditorStyle (ol5Style) {
  const textStyle = ol5Style.getText();
  const backgroundFill = textStyle.getBackgroundFill();
  const ol5Align = `${textStyle.getTextBaseline()} ${textStyle.getTextAlign()}`;
  return {
    'color': textStyle.getFill().getColor(),
    'background-color': backgroundFill ? backgroundFill.getColor() : '',
    'font': textStyle.getFont(),
    'transform-origin': _mapOl5TextAlignToTransformOrigin(ol5Align),
    'transform': _mapOl5TextAlignToTranslate(ol5Align)
  };
}

const ol5TextAlignToTransformOriginMap = {
  'top left': 'bottom right',
  'top center': 'bottom',
  'top right': 'bottom left',
  'alphabetic left': 'right',
  'alphabetic center': 'initial',
  'alphabetic right': 'left',
  'bottom left': 'top right',
  'bottom center': 'top',
  'bottom right': 'top left'
};

const ol5TextAlignToTranslateMap = {
  'top left': [ 0, 0 ],
  'top center': [ -50, 0 ],
  'top right': [ -100, 0 ],
  'alphabetic left': [ 0, -80 ],
  'alphabetic center': [ -50, -80 ],
  'alphabetic right': [ -100, -80 ],
  'bottom left': [ 0, -100 ],
  'bottom center': [ -50, -100 ],
  'bottom right': [ -100, -100 ]
};

function _mapOl5TextAlignToTransformOrigin (ol5Align) {
  return ol5TextAlignToTransformOriginMap[ol5Align] || 'initial';
}

function _mapOl5TextAlignToTranslate (ol5Align) {
  const translateCoordinates = ol5TextAlignToTranslateMap[ol5Align] || [ -50, -50 ];
  return `translate(${translateCoordinates[0]}%, ${translateCoordinates[1]}%)`;
}

function placeCaretAtEnd (element) {
  const range = document.createRange();
  range.selectNodeContents(element);
  range.collapse(false);
  const sel = window.getSelection();
  sel.removeAllRanges();
  sel.addRange(range);
}
